import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Check, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminApprovals = () => {
  const [drafts, setDrafts] = useState(null);
  const [edits, setEdits] = useState({});
  const [busy, setBusy] = useState(null);

  const load = () => axios.get(`${API}/portal/admin/approvals`, { withCredentials: true })
    .then((r) => {
      setDrafts(r.data.drafts);
      setEdits(Object.fromEntries(r.data.drafts.map((d) => [d.id, d.ai_text])));
    }).catch(() => setDrafts([]));

  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    setBusy(id);
    try {
      await axios.post(`${API}/portal/admin/approvals/${id}/approve`, { edited_text: edits[id] }, { withCredentials: true });
      toast.success("Approved — the client can now see this update.");
      load();
    } catch {
      toast.error("Approval failed.");
    } finally {
      setBusy(null);
    }
  };

  const regenerate = async (id) => {
    setBusy(id);
    try {
      const r = await axios.post(`${API}/portal/admin/approvals/${id}/regenerate`, {}, { withCredentials: true });
      setEdits({ ...edits, [id]: r.data.ai_text });
      toast.success("New draft generated from verified activity.");
    } catch {
      toast.error("Regenerate failed.");
    } finally {
      setBusy(null);
    }
  };

  if (drafts === null) return <Loader2 className="h-5 w-5 animate-spin text-slate-500" />;

  return (
    <div data-testid="admin-approvals">
      <p className="text-sm text-slate-500 mb-5">
        Claude drafts daily updates from verified checklist activity only. Nothing reaches a client until you approve it here.
      </p>
      {drafts.length === 0 ? (
        <p className="text-sm text-slate-500" data-testid="approvals-empty">No drafts waiting for approval.</p>
      ) : (
        <div className="space-y-4">
          {drafts.map((d) => (
            <div key={d.id} className="rounded-2xl border border-white/10 p-5" data-testid={`approval-card-${d.id.slice(0, 8)}`}>
              <p className="font-mono-label text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-3">
                {d.client_name} · Day {d.day_number} · {d.date}
              </p>
              <textarea
                rows={5}
                value={edits[d.id] ?? d.ai_text}
                onChange={(e) => setEdits({ ...edits, [d.id]: e.target.value })}
                className="w-full rounded-xl border border-white/15 bg-white/[0.04] px-4 py-3 text-sm text-white leading-relaxed outline-none focus:border-white/40 resize-y"
                data-testid={`approval-text-${d.id.slice(0, 8)}`}
              />
              <div className="mt-3 flex gap-2">
                <button
                  onClick={() => approve(d.id)} disabled={busy === d.id}
                  data-testid={`approval-approve-${d.id.slice(0, 8)}`}
                  className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-[#060D1A] transition-colors duration-300 hover:bg-slate-300 disabled:opacity-50"
                >
                  {busy === d.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                  Approve & Publish
                </button>
                <button
                  onClick={() => regenerate(d.id)} disabled={busy === d.id}
                  data-testid={`approval-regen-${d.id.slice(0, 8)}`}
                  className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-sm font-semibold text-slate-200 transition-colors duration-300 hover:bg-white hover:text-[#060D1A] disabled:opacity-50"
                >
                  <RefreshCw className="h-4 w-4" /> Regenerate
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
